import { apiService } from './api';

/**
 * Response types for deploy-related API operations
 */
export interface PresignedUrlResponse {
  presignedUrl: string;
  expires?: number;
}

export interface PresignedUrlErrorResponse {
  message: string;
  code?: string;
  currentReleases?: number;
  maxReleases?: number;
  error?: {
    code?: string;
    currentReleases?: number;
    maxReleases?: number;
    oldestRelease?: {
      deployId: string;
      createdAt: string;
      sizeBytes: number;
    };
  };
}

export interface DeleteReleaseResponse {
  freedBytes: number;
  deletedAt: string;
  message?: string;
}

export interface DeployError {
  code?: string;
  message?: string;
  details?: Record<string, any>;
}

/**
 * Service for deploy-related operations
 * Handles presigned URL retrieval and release deletion
 */
export class DeployService {
  /**
   * Get presigned URL for uploading a new deploy
   * May return 409 if storage limits are reached
   * @param deployId Deployment ID
   * @returns Promise with presigned URL
   */
  async getPresignedUrl(deployId: string): Promise<PresignedUrlResponse> {
    try {
      return await apiService.post<PresignedUrlResponse>(
        `/v1/deploys/${deployId}/presigned-url`
      );
    } catch (error: any) {
      // Re-throw to allow caller to handle specific error codes
      throw error;
    }
  }

  /**
   * Delete a specific release/deployment
   * Used when storage limits are reached to free up space
   * @param deployId ID of the deployment to delete
   * @returns Promise with freed space info
   */
  async deleteRelease(deployId: string): Promise<DeleteReleaseResponse> {
    try {
      return await apiService.delete<DeleteReleaseResponse>(
        `/v1/deploys/${deployId}`
      );
    } catch (error: any) {
      // Handle specific error cases
      if (error.status === 404) {
        throw new Error('Release not found');
      }
      if (error.status === 409) {
        throw new Error('Release is currently in use and cannot be deleted');
      }
      throw error;
    }
  }

  /**
   * Extract error details from presigned URL response
   * @param error API error object
   * @returns Structured error information
   */
  static parsePresignedUrlError(error: any): {
    code?: string;
    currentReleases?: number;
    maxReleases?: number;
    oldestRelease?: {
      deployId: string;
      createdAt: string;
      sizeBytes: number;
    };
  } {
    // Try to get error from response body
    if (error.errorData?.error) {
      return error.errorData.error;
    }

    // Fallback to error object itself
    if (error.error) {
      return error.error;
    }

    return {};
  }
}

export const deployService = new DeployService();
